import React, { FunctionComponent, ReactText, useEffect, useState } from 'react';
import { SbBlokData, storyblokEditable } from '@storyblok/react';
import classnames from 'classnames';

import {
  AppBar,
  Button,
  ButtonProps as ButtonComponentProps,
  Drawer,
  Nav,
  NavItem,
  RaisinLogo,
  TertiaryButton
} from '@savebetter/components';

import customConfig from '../../../config';

import { LinkProps } from './Link';
import { MediaProps } from './Media';

interface ButtonProps {
  config?: ButtonComponentProps;
  value: ReactText;
}

export interface FooterButtonsProps {
  primaryButton?: ButtonProps;
  secondaryButton?: ButtonProps;
}

interface NavItemType {
  label: string;
  url: string;
  active: boolean;
}

export interface MobileNavigationProps {
  className?: string;
  navItems: Array<NavItemType>;
  buttonProps?: FooterButtonsProps;
  location: string;
}

interface NavigationElement extends SbBlokData {
  link: {
    label: string;
    cached_url: string;
  };
  navigationElements: NavigationElement[];
  label: string;
}

interface Blok extends SbBlokData {
  logoImage: MediaProps[];
  primaryButton?: LinkProps[];
  secondaryButton?: LinkProps[];
  navigationElements: NavigationElement[];
}

const prepareButtonProps = ({ primaryButton = [], secondaryButton = [] }: Partial<Blok>): FooterButtonsProps => {
  let buttons: FooterButtonsProps = {};

  if (primaryButton.length > 0) {
    buttons = {
      ...buttons,
      primaryButton: {
        value: primaryButton[0].text,
        config: {
          href: `${customConfig.postLoginUrl}/auth/login`
        }
      }
    };
  }

  if (secondaryButton.length > 0) {
    buttons = {
      ...buttons,
      secondaryButton: {
        value: secondaryButton[0].text,
        config: {
          href: secondaryButton[0].url
        }
      }
    };
  }

  return buttons;
};

const primaryButtonClassnames = (secondaryButton: boolean): string =>
  classnames('md:w-full lg:w-auto', {
    ['lg:ml-10']: secondaryButton
  });

const FooterButtons: FunctionComponent<React.PropsWithChildren<FooterButtonsProps>> = ({
  primaryButton,
  secondaryButton
}) => (
  <div className="flex flex-col lg:flex-row mt-8 md:mt-0 lg:m-0 items-center px-4 lg:px-0">
    {primaryButton && (
      <Button {...primaryButton.config} size="small" className={primaryButtonClassnames(Boolean(secondaryButton))}>
        {primaryButton.value}
      </Button>
    )}
    {primaryButton && secondaryButton && <div className="w-6 h-10 lg:h-0" />}
    {secondaryButton && (
      <Button {...secondaryButton.config} size="small" kind="secondary">
        {secondaryButton.value}
      </Button>
    )}
  </div>
);

const prepareNavLinks = (sblinks: NavigationElement[], location: string): NavItemType[] => {
  const preparedLinks = [];

  for (const link of sblinks) {
    preparedLinks.push({
      label: link.label,
      url: '/' + link.link.cached_url,
      active: location === `/${link.link.cached_url}`
    });
  }

  return preparedLinks;
};

const MobileNavigation: FunctionComponent<React.PropsWithChildren<MobileNavigationProps>> = ({
  navItems,
  buttonProps,
  location
}) => {
  const [drawerVisibility, setDrawerVisibility] = useState<boolean>(false);

  return (
    <>
      <TertiaryButton
        title="Close button"
        icon="Hamburger"
        onClick={(): void => setDrawerVisibility(true)}
        data-testid="drawer-hamburger"
        aria-label="Open the navigation"
      />
      <Drawer
        position="right"
        buttonClassName="mt-0"
        onClose={(): void => setDrawerVisibility(false)}
        open={drawerVisibility}
        logo={
          <>
            <RaisinLogo className="block md:hidden" />
            <NavItem href="/" description="Home" className="hidden md:block -ml-5" active={location === '/'} />
          </>
        }
      >
        <div className="flex flex-col">
          {navItems.length > 0 && (
            <>
              <Nav
                className="pb-4 block md:hidden"
                direction="vertical"
                items={[{ label: 'Home', url: '/', active: location === '/' }, ...navItems]}
                aria-label="Main"
              />
              <Nav className="pb-4 hidden md:block" direction="vertical" items={navItems} aria-label="Main" />
            </>
          )}
          {buttonProps && <FooterButtons {...buttonProps} />}
        </div>
      </Drawer>
    </>
  );
};

export const Navigation: FunctionComponent<React.PropsWithChildren<{ blok: Blok }>> = ({ blok }) => {
  const raisinLogoDimension = {
    sm: { height: '45px', width: '240px' },
    md: { height: '45px', width: '240px' },
    lg: { height: '45px', width: '240px' }
  };
  const [location, setLocation] = useState('');
  const { navigationElements = [], primaryButton = [], secondaryButton = [] } = blok;
  const buttonProps = prepareButtonProps({ primaryButton, secondaryButton });
  const isMobileApp = typeof window !== 'undefined' && localStorage.getItem('mobileApp') === 'true';

  useEffect(() => {
    if (window) {
      setLocation(window.location.pathname);
    }
  }, []);

  if (isMobileApp) {
    return null;
  }

  return (
    <AppBar className="lg:pb-0 lg:px-0 md:pl-0 md:pr-0 sm:px-0 align-center box-border" {...storyblokEditable(blok)}>
      <a href="/">
        <RaisinLogo dimension={raisinLogoDimension} />
      </a>
      <>
        <div className="hidden lg:flex flex-row items-center">
          <Nav
            data-testid="navigation"
            items={prepareNavLinks(navigationElements, location)}
            className="mr-18"
            aria-label="Main"
          />
          {primaryButton && <FooterButtons {...buttonProps} />}
        </div>
        <div className="lg:hidden flex items-center">
          <MobileNavigation
            navItems={prepareNavLinks(navigationElements, location)}
            buttonProps={buttonProps}
            location={location}
          />
        </div>
      </>
    </AppBar>
  );
};
